import React, { useState } from 'react';
import { useClientContactsByClient, deleteClientContact } from '../useData';
import { Button, ConfirmationDialog } from '../../../components/ui';
import { useNotification } from '../../../contexts/NotificationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import ContactPointsDisplay from '../../address-book/ContactPointsDisplay';
import ClientContactFormModal from './ClientContactFormModal';
import {
  cardRowClass,
  cardSurfaceClass,
  deleteActionClass,
  editActionClass,
  mutedTextClass,
  secondaryTextClass,
} from '../../address-book/themeHelpers';

interface ClientContactsSectionProps {
  clientId: number;
}

const ClientContactsSection: React.FC<ClientContactsSectionProps> = ({ clientId }) => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const { data: contacts, isLoading, mutate } = useClientContactsByClient(clientId);
  const [contactToDeleteId, setContactToDeleteId] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingContactId, setEditingContactId] = useState<number | null>(null);

  const handleDeleteConfirm = async () => {
    const id = contactToDeleteId;
    if (id == null) return;
    setContactToDeleteId(null);
    try {
      await deleteClientContact(id);
      showSuccess('Person removed successfully');
      mutate();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to remove person';
      showError(message);
    }
  };

  const openCreateForm = () => {
    setEditingContactId(null);
    setFormOpen(true);
  };

  const openEditForm = (id: number) => {
    setEditingContactId(id);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingContactId(null);
  };

  if (isLoading) {
    return (
      <div className={cardSurfaceClass(colors)}>
        <h3 className={cn('text-lg font-semibold mb-4', colors.text)}>People</h3>
        <p className={cn('text-sm', mutedTextClass(colors))}>Loading contacts...</p>
      </div>
    );
  }

  return (
    <>
      <div className={cardSurfaceClass(colors)}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={cn('text-lg font-semibold', colors.text)}>
            People ({contacts.length})
          </h3>
          <Button variant="default" size="sm" onClick={openCreateForm}>
            Add person
          </Button>
        </div>

        {contacts.length === 0 ? (
          <p className={cn('text-sm', mutedTextClass(colors))}>No people linked to this organization yet.</p>
        ) : (
          <div className="space-y-3">
            {contacts.map((contact) => {
              const name = `${contact.clientContactFirstName || ''} ${contact.clientContactLastName || ''}`.trim();
              return (
                <div
                  key={contact.clientContactId}
                  className={cardRowClass(colors)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className={cn('font-medium', colors.text)}>
                        {name || `Person #${contact.clientContactId}`}
                      </p>
                      {contact.clientContactPosition && (
                        <p className={cn('text-sm mt-1', secondaryTextClass(colors))}>{contact.clientContactPosition}</p>
                      )}
                      <div className="mt-2 space-y-1">
                        <ContactPointsDisplay
                          points={contact.contactPoints}
                          kind="EMAIL"
                        />
                        <ContactPointsDisplay
                          points={contact.contactPoints}
                          kind="PHONE"
                        />
                      </div>
                      {contact.clientContactNotes && (
                        <p className={cn('text-xs mt-2 line-clamp-2', mutedTextClass(colors))}>
                          {contact.clientContactNotes}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        type="button"
                        onClick={() => openEditForm(contact.clientContactId)}
                        className={editActionClass(colors)}
                        title="Edit"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => setContactToDeleteId(contact.clientContactId)}
                        className={deleteActionClass(colors)}
                        title="Delete"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ClientContactFormModal
        isOpen={formOpen}
        onClose={closeForm}
        clientId={clientId}
        contactId={editingContactId}
        onSaved={() => mutate()}
      />

      <ConfirmationDialog
        isOpen={contactToDeleteId !== null}
        onClose={() => setContactToDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title="Remove person"
        message="Are you sure you want to remove this person from the address book?"
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </>
  );
};

export default ClientContactsSection;
